// src/pages/admin/bookings/BookingsListPage.tsx

import * as React from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Typography,
    Tooltip,
    Chip,
    Card,
    CardContent,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import BookingStatusChip from "./components/BookingStatusChip";
import BookingDates from "./components/BookingDates";
import {
    BookingListItem,
    DEFAULT_BOOKING_STATUSES,
    PagedResponse,
    safeStr,
} from "./bookingTypes";

import * as AdminFetchMod from "../../../services/adminFetch";

function adminFetchLocal(path: string, init: RequestInit = {}) {
    const raw = process.env.REACT_APP_API_BASE || "https://www.ritasproperties.com";
    const API_BASE = raw.replace(/\/+$/, "");
    const url = `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;

    const headers = new Headers(init.headers || {});
    headers.set("Accept", "application/json");

    console.log("[adminFetchLocal] ->", init.method || "GET", url);
    return fetch(url, { ...init, headers, credentials: "include" });
}

function useDebouncedValue<T>(value: T, delayMs: number) {
    const [v, setV] = React.useState(value);
    React.useEffect(() => {
        const t = window.setTimeout(() => setV(value), delayMs);
        return () => window.clearTimeout(t);
    }, [value, delayMs]);
    return v;
}

export default function AdminBookingsListPage() {
    const { locale } = useParams<{ locale: string }>();
    const [sp, setSp] = useSearchParams();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // resolve adminFetch safely
    const adminFetch =
        (AdminFetchMod as any)?.adminFetch && typeof (AdminFetchMod as any).adminFetch === "function"
            ? ((AdminFetchMod as any).adminFetch as (path: string, init?: RequestInit) => Promise<Response>)
            : adminFetchLocal;

    const q = sp.get("q") || "";
    const status = sp.get("status") || "";
    const from = sp.get("from") || "";
    const to = sp.get("to") || "";
    const limit = Math.max(1, Number(sp.get("limit") || 30));
    const offset = Math.max(0, Number(sp.get("offset") || 0));

    const [qDraft, setQDraft] = React.useState(q);
    React.useEffect(() => setQDraft(q), [q]);
    const qDebounced = useDebouncedValue(qDraft, 350);

    const [loading, setLoading] = React.useState(false);
    const [err, setErr] = React.useState<string | null>(null);
    const [items, setItems] = React.useState<BookingListItem[]>([]);
    const [total, setTotal] = React.useState<number>(0);

    const buildUrl = React.useCallback(() => {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (status) p.set("status", status);
        if (from) p.set("from", from);
        if (to) p.set("to", to);
        p.set("limit", String(limit));
        p.set("offset", String(offset));
        return `/api/admin/bookings?${p.toString()}`;
    }, [q, status, from, to, limit, offset]);

    const fetchList = React.useCallback(async () => {
        setLoading(true);
        setErr(null);
        const url = buildUrl();

        try {
            const res = await adminFetch(url, { method: "GET" });
            const txt = await res.text();
            let j: any = {};
            try {
                j = txt ? JSON.parse(txt) : {};
            } catch {
                j = { _raw: txt };
            }

            if (!res.ok) throw new Error(j?.error || `Request failed (${res.status})`);
            const data = j as PagedResponse<BookingListItem>;

            setItems(Array.isArray(data.items) ? data.items : []);
            setTotal(typeof data.total === "number" ? data.total : 0);
        } catch (e: any) {
            setErr(e?.message || "Failed to load bookings.");
            setItems([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [adminFetch, buildUrl]);

    React.useEffect(() => {
        fetchList();
    }, [fetchList]);

    // apply debounced q -> URL params
    React.useEffect(() => {
        const current = sp.get("q") || "";
        if (qDebounced !== current) {
            const next = new URLSearchParams(sp);
            if (qDebounced) next.set("q", qDebounced);
            else next.delete("q");
            next.set("offset", "0");
            setSp(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qDebounced]);

    const goPage = (dir: -1 | 1) => {
        const next = new URLSearchParams(sp);
        const newOffset = Math.max(0, offset + dir * limit);
        next.set("offset", String(newOffset));
        setSp(next);
    };

    const setParam = (k: string, v: string) => {
        const next = new URLSearchParams(sp);
        if (v) next.set(k, v);
        else next.delete(k);
        next.set("offset", "0");
        setSp(next);
    };

    const clearFilters = () => {
        const next = new URLSearchParams();
        next.set("limit", String(limit));
        next.set("offset", "0");
        setSp(next);
        setQDraft("");
    };

    const pageFrom = total === 0 ? 0 : offset + 1;
    const pageTo = Math.min(offset + limit, total);

    return (
        <Stack spacing={2}>
            {/* Top card */}
            <Paper
                elevation={0}
                variant="outlined"
                sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    borderColor: "divider",
                    background:
                        "radial-gradient(1000px 600px at 20% -10%, rgba(25,118,210,0.10), transparent 60%), radial-gradient(800px 500px at 90% 0%, rgba(46,125,50,0.10), transparent 55%)",
                }}
            >
                <Toolbar sx={{ gap: 1.5, flexWrap: "wrap", py: 1.5 }}>
                    <Stack spacing={0}>
                        <Typography variant="h6" sx={{ fontWeight: 950, letterSpacing: 0.2 }}>
                            Bookings
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage confirmed stays, holds, and upcoming check-ins.
                        </Typography>
                    </Stack>

                    <Box sx={{ flex: 1 }} />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
                        <Button
                            fullWidth={isMobile}
                            component={RouterLink}
                            to={`/${locale}/admin/bookings/calendar`}
                            startIcon={<CalendarMonthIcon />}
                            variant="outlined"
                            sx={{ borderRadius: 2, whiteSpace: "nowrap" }}
                        >
                            Calendar
                        </Button>

                        <Button
                            fullWidth={isMobile}
                            component={RouterLink}
                            to={`/${locale}/admin/bookings/new`}
                            startIcon={<AddIcon />}
                            variant="contained"
                            sx={{ borderRadius: 2, fontWeight: 900, whiteSpace: "nowrap" }}
                        >
                            New booking
                        </Button>

                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchList} sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Toolbar>

                <Divider />

                {/* Filters */}
                <Box sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                        <TextField
                            size="small"
                            fullWidth
                            value={qDraft}
                            onChange={(e) => setQDraft(e.target.value)}
                            placeholder="Search property / customer…"
                            InputProps={{
                                startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 8 }} />,
                                endAdornment: qDraft ? (
                                    <IconButton size="small" onClick={() => setQDraft("")}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                ) : undefined,
                            }}
                        />

                        <Box
                            sx={{
                                display: "grid",
                                gap: 1.5,
                                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
                                alignItems: "center",
                            }}
                        >
                            <TextField
                                size="small"
                                select
                                label="Status"
                                value={status}
                                onChange={(e) => setParam("status", e.target.value)}
                            >
                                {DEFAULT_BOOKING_STATUSES.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                size="small"
                                label="From"
                                type="date"
                                value={from}
                                onChange={(e) => setParam("from", e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />

                            <TextField
                                size="small"
                                label="To"
                                type="date"
                                value={to}
                                onChange={(e) => setParam("to", e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />

                            <Button
                                fullWidth={isMobile}
                                onClick={clearFilters}
                                variant="text"
                                sx={{ borderRadius: 2, fontWeight: 800, justifySelf: { md: "end" } }}
                            >
                                Clear
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Paper>

            {err ? <Alert severity="error">{err}</Alert> : null}

            {/* Results */}
            <Paper elevation={0} variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", borderColor: "divider" }}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                    gap={1}
                    sx={{ px: 2, py: 1.5 }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                        Results
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Chip
                            label={total ? `${pageFrom}–${pageTo} of ${total}` : "0 results"}
                            variant="outlined"
                            sx={{ borderRadius: 999, fontWeight: 800 }}
                        />
                        <IconButton onClick={() => goPage(-1)} disabled={loading || offset === 0} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                        <IconButton onClick={() => goPage(1)} disabled={loading || offset + limit >= total} size="small">
                            <ArrowForwardIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                <Divider />

                {/* Mobile cards */}
                {isMobile ? (
                    <Box sx={{ p: 2 }}>
                        {loading ? (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="text.secondary">
                                    Loading…
                                </Typography>
                            </Stack>
                        ) : items.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                No bookings found.
                            </Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                {items.map((b) => {
                                    const pid = b.property_id ?? null;
                                    const title = safeStr(b.property_title, pid ? `Property #${pid}` : "—");
                                    const slug = b.property_slug ?? null;

                                    return (
                                        <Card key={b.id} variant="outlined" sx={{ borderRadius: 3 }}>
                                            <CardContent sx={{ p: 1.5 }}>
                                                <Stack spacing={1}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                                        <Box>
                                                            <Typography sx={{ fontWeight: 900 }}>#{b.id}</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                                                {title}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                                                                {slug ? `/properties/${slug}` : "—"}
                                                            </Typography>
                                                        </Box>

                                                        <BookingStatusChip status={b.status ?? null} />
                                                    </Stack>

                                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                        <Chip size="small" label={`Guests: ${b.guests_total ?? "—"}`} variant="outlined" />
                                                        <Chip
                                                            size="small"
                                                            label={`${b.check_in ?? "—"} → ${b.check_out ?? "—"}`}
                                                            variant="outlined"
                                                        />
                                                    </Stack>

                                                    <Stack direction="row" spacing={1}>
                                                        <Button
                                                            fullWidth
                                                            component={RouterLink}
                                                            to={`/${locale}/admin/bookings/${b.id}`}
                                                            size="small"
                                                            variant="contained"
                                                            sx={{ borderRadius: 2, fontWeight: 900 }}
                                                        >
                                                            View
                                                        </Button>

                                                        {slug ? (
                                                            <Tooltip title="Open property">
                                                                <IconButton component={RouterLink} to={`/${locale}/properties/${slug}`} size="small">
                                                                    <OpenInNewIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : null}
                                                    </Stack>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>
                ) : (
                    /* Desktop table */
                    <TableContainer sx={{ overflowX: "auto" }}>
                        <Table size="small" sx={{ minWidth: 900 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 900 }}>ID</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }}>Property</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }}>Dates</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }}>Guests</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }} align="right">
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                                                <CircularProgress size={18} />
                                                <Typography variant="body2" color="text.secondary">
                                                    Loading…
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ) : items.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                No bookings found.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    items.map((b) => {
                                        const pid = b.property_id ?? null;
                                        const title = safeStr(b.property_title, pid ? `Property #${pid}` : "—");
                                        const slug = b.property_slug ?? null;

                                        return (
                                            <TableRow key={b.id} hover>
                                                <TableCell sx={{ fontWeight: 900 }}>{b.id}</TableCell>
                                                <TableCell>
                                                    <Stack spacing={0.25}>
                                                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                                            {title}
                                                        </Typography>
                                                        {slug ? (
                                                            <Typography variant="caption" color="text.secondary">
                                                                /properties/{slug}
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="caption" color="text.secondary">
                                                                —
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </TableCell>

                                                <TableCell>
                                                    <BookingDates checkIn={b.check_in} checkOut={b.check_out} />
                                                </TableCell>

                                                <TableCell>{b.guests_total ?? "—"}</TableCell>

                                                <TableCell>
                                                    <BookingStatusChip status={b.status ?? null} />
                                                </TableCell>

                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        <Button
                                                            component={RouterLink}
                                                            to={`/${locale}/admin/bookings/${b.id}`}
                                                            size="small"
                                                            variant="contained"
                                                            sx={{ borderRadius: 2, fontWeight: 900 }}
                                                        >
                                                            View
                                                        </Button>

                                                        {slug ? (
                                                            <Tooltip title="Open property">
                                                                <IconButton component={RouterLink} to={`/${locale}/properties/${slug}`} size="small">
                                                                    <OpenInNewIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : null}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Divider />

                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    gap={1}
                    sx={{ px: 2, py: 1.5 }}
                >
                    <Typography variant="caption" color="text.secondary">
                        Tip: use date range to isolate overlapping stays.
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                        <Button
                            fullWidth={isMobile}
                            size="small"
                            variant="outlined"
                            onClick={() => goPage(-1)}
                            disabled={loading || offset === 0}
                            sx={{ borderRadius: 2, fontWeight: 800 }}
                        >
                            Prev
                        </Button>
                        <Button
                            fullWidth={isMobile}
                            size="small"
                            variant="outlined"
                            onClick={() => goPage(1)}
                            disabled={loading || offset + limit >= total}
                            sx={{ borderRadius: 2, fontWeight: 800 }}
                        >
                            Next
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        </Stack>
    );
}
